/**
 * PO status colors and utilities for the dashboard.
 * Colors match backend status_config.py.
 */

import { formatStatus } from '@/lib/utils'

// Hex colors for every PO status — sourced from status_config.py
export const PO_STATUS_COLORS: Record<string, string> = {
  // Pre-PO
  generated: '#94a3b8',
  pending: '#94a3b8',
  spec_credit_pending: '#a78bfa',
  spec_credit_sent: '#a78bfa',
  spec_credit_responded: '#a78bfa',
  email_sent: '#60a5fa',
  // Processing
  online_order_placed: '#38bdf8',
  asked_for_spec: '#818cf8',
  po_cut: '#34d399',
  po_processing: '#fbbf24',
  on_hold_settling: '#fb923c',
  pull_from_warehouse: '#a78bfa',
  // Shipping
  need_to_schedule: '#f472b6',
  freight_booked: '#2dd4bf',
  in_transit_to_site: '#38bdf8',
  partially_in_transit: '#93c5fd',
  partially_delivered: '#86efac',
  delivered_to_site: '#22c55e',
  at_terminal_awaiting: '#fbbf24',
  // Returns / Issues
  return_in_progress: '#f87171',
  exchange_in_progress: '#fb923c',
  returned: '#ef4444',
  order_cancelled: '#ef4444',
  waiting_on_install: '#a78bfa',
  cust_serv_required: '#f87171',
  warranty_claim: '#f87171',
  disregard: '#94a3b8',
  // Moved to another supplier; a new PO took its place (USA-1689).
  replaced: '#94a3b8',
  // Legacy
  po_held: '#fb923c',
  cancelled: '#ef4444',
  acknowledged: '#38bdf8',
  confirmed: '#34d399',
  shipped: '#38bdf8',
  received: '#22c55e',
  delivered: '#22c55e',
}

// Pipeline category colors for project-level badges
export const PIPELINE_CATEGORY_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  unsent: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Unsent' },
  awaiting_spec: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Awaiting Spec' },
  awaiting_reply: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Awaiting Reply' },
  awaiting: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Awaiting Reply' },
  processing: { bg: 'bg-sky-100', text: 'text-sky-800', label: 'Processing' },
  shipping: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Shipping' },
  delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'Delivered' },
  issues: { bg: 'bg-red-100', text: 'text-red-800', label: 'Issues' },
  order_cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Order Cancelled' },
}

/**
 * Get the inline style for a PO status badge.
 */
export function getPoStatusStyle(status: string | null | undefined): React.CSSProperties {
  const color = PO_STATUS_COLORS[status ?? ''] ?? '#94a3b8'
  return {
    backgroundColor: color + '20',
    color,
    borderColor: color + '40',
  }
}

/**
 * Format a PO status for display.
 */
export function formatPoStatus(status: string | null | undefined): string {
  return formatStatus(status)
}

// Compatibility exports for shared invoice badges and existing cross-feature consumers.
export {
  getInvoiceStatusStyle,
  formatInvoiceStatus,
  getInvoicePriorityStyle,
  formatInvoicePriority,
} from '@/features/invoices/status'

// Mirrors CLOSED_ORDER_STATUSES, DELIVERED_PO_STATUSES and SETTLED_PO_STATUSES in
// the backend's Active project filter (backend/api/app/routers/dashboard/projects.py).
const CLOSED_ORDER_STATUSES = new Set(['cancelled', 'refunded', 'delivered'])
const DELIVERED_PO_STATUSES = new Set(['delivered_to_site', 'delivered', 'received'])
const SETTLED_PO_STATUSES = new Set(['cancelled', 'order_cancelled', 'disregard', 'replaced'])

type DisplayStatusOrder = {
  status?: string | null
  purchase_orders?: readonly { status?: string | null }[] | null
}

/** Every PO on the order is delivered or cancelled, and at least one was delivered. */
function deliveredByPurchaseOrders(order: DisplayStatusOrder): boolean {
  const statuses = (order.purchase_orders ?? []).map((po) => po.status ?? '')
  return (
    statuses.some((status) => DELIVERED_PO_STATUSES.has(status)) &&
    statuses.every((status) => DELIVERED_PO_STATUSES.has(status) || SETTLED_PO_STATUSES.has(status))
  )
}

function isDelivered(order: DisplayStatusOrder): boolean {
  return order.status === 'delivered' || deliveredByPurchaseOrders(order)
}

/**
 * Status for the project header. An 'active' project whose orders are all
 * closed (cancelled, refunded, or delivered — by status or because every PO
 * was delivered) is left out of the Active filter, so it reads as Delivered
 * (any order delivered) or Cancelled instead of Active.
 */
export function projectDisplayStatus(
  status: string,
  orders: readonly DisplayStatusOrder[],
): string {
  if (status !== 'active' || orders.length === 0) return status
  const closed = (order: DisplayStatusOrder) =>
    CLOSED_ORDER_STATUSES.has(order.status ?? '') || isDelivered(order)
  if (!orders.every(closed)) return status
  return orders.some(isDelivered) ? 'delivered' : 'cancelled'
}
